// Notifications — the signed-in user's notification feed behind the header
// bell. POST notifications-history/my-notifications takes `{ page, limit }`
// and returns `{ items, totalRecords }`.
//
// `title` and the optional `message` arrive as READY-TO-DISPLAY text (e.g.
// "Guest export completed") and render literally — backend data values stay
// literal, no i18n keys for them. `module` and `status` are confirmed enums
// and drive the row treatment (icon by module, tone by status) for the
// modules whose enhanced render has shipped (export, import); the rest render
// the plain text line until their own pass. The per-module `details` object
// is typed only where a row consumes it: export items carry
// `{ exportId, fileName?, rows? }`, and GET export-manager/:id/download-url
// resolves that id to `{ downloadUrl }` for the row's Download action.
// Other modules' details (inspection ids…) stay unmapped for now. The
// confirmed sample carries NO timestamp field, so no time is rendered.
//
// The list is fetched LAZILY — only when the bell popup opens (user
// requirement: no request without opening it), so the query ships as a lazy
// hook only. The unread COUNT is not derived from the page: every response
// carries the server total in the `custom-notifications` header, mirrored
// into notificationsSlice by axiosBaseQuery, and the bell/panel read it there.
//
// Read state: PATCH notifications-history/read-all marks everything read (no
// body); PATCH notifications-history/:id/read marks one — clicking an unread
// row sends it.
//
// Both read mutations are optimistic: their outcome (rows flip to read, the
// count drops) is fully known client-side, so the cache AND the slice count
// are patched directly and undone on failure — no LIST invalidation. The
// PATCH response's own header then re-syncs the count from the server.

import type { RootState } from '@/store/store';
import { setUnreadCount } from '@/redux/notifications/notificationsSlice';
import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

/** The wire's `module` — which feature emitted the notification. */
export enum NotificationModule {
  IMPORT = 'import',
  EXPORT = 'export',
  INSPECTIONS = 'inspections',
  SUBMISSIONS = 'submissions',
}

/** The wire's `status` — where the emitting job/event stands. */
export enum NotificationStatus {
  STARTED = 'started',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/** The export notification's `details` — the id the Download action resolves. */
export interface ExportNotificationDetails {
  exportId: string;
  /** Present on completed items (e.g. "guests-….csv"); absent on started. */
  fileName?: string;
}

export interface AppNotification {
  id: string;
  module: NotificationModule;
  status: NotificationStatus;
  /** Backend-authored copy, rendered literally — the row's first line. */
  title: string;
  /** Optional secondary line (absent on export items). */
  message?: string;
  /** Mapped only for module=EXPORT — drives the row's Download action. */
  exportDetails?: ExportNotificationDetails;
  isRead: boolean;
}

export interface NotificationPage {
  items: AppNotification[];
  /** `totalRecords` — the full feed size, not just this page. */
  total: number;
}

/** The single arg the app queries with — the dropdown shows one page. Shared
 *  so readAll's optimistic patch targets the identical cache key. */
export const FIRST_PAGE = { page: 1, limit: 20 } as const;

// ---- raw shapes (per the confirmed response) ----
interface ApiNotification {
  _id: string;
  module: NotificationModule;
  status: NotificationStatus;
  title: string;
  /** Absent on some modules (export items carry only `title`). */
  message?: string;
  /** Per-module payload — e.g. `{ inspectionId, referenceNumber }` or
   *  `{ exportId, fileName, rows }`. */
  details?: Record<string, unknown>;
  isRead: boolean;
}
interface ApiNotificationPage {
  items: ApiNotification[];
  totalRecords: number;
}

const toNotification = (n: ApiNotification): AppNotification => ({
  id: n._id,
  module: n.module,
  status: n.status,
  title: n.title,
  message: n.message,
  exportDetails:
    n.module === NotificationModule.EXPORT && typeof n.details?.exportId === 'string'
      ? {
          exportId: n.details.exportId,
          fileName: typeof n.details.fileName === 'string' ? n.details.fileName : undefined,
        }
      : undefined,
  isRead: n.isRead,
});

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getMyNotifications: build.query<NotificationPage, { page: number; limit: number }>({
      query: (body) => ({ ...ROUTES.notifications.list, data: body }),
      transformResponse: (res: ApiNotificationPage) => ({
        items: res.items.map(toNotification),
        total: res.totalRecords,
      }),
      providesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),
    getExportDownloadUrl: build.query<string, string>({
      query: (exportId) => ({ ...ROUTES.exportManager.downloadUrl(exportId) }),
      transformResponse: (res: { downloadUrl: string }) => res.downloadUrl,
      keepUnusedDataFor: 0, // one-shot artefact URL — never serve it stale
    }),
    readNotification: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.notifications.readOne(id) }),
      onQueryStarted: async (id, { dispatch, getState, queryFulfilled }) => {
        let flipped = false;
        const patch = dispatch(
          notificationsApi.util.updateQueryData('getMyNotifications', FIRST_PAGE, (draft) => {
            const row = draft.items.find((n) => n.id === id);
            if (row && !row.isRead) {
              row.isRead = true;
              flipped = true;
            }
          }),
        );
        const prev = (getState() as RootState).notifications.unreadCount;
        if (flipped) dispatch(setUnreadCount(prev - 1));
        try {
          await queryFulfilled;
        } catch {
          patch.undo(); // the component owns the error toast
          if (flipped) dispatch(setUnreadCount(prev));
        }
      },
    }),
    readAllNotifications: build.mutation<unknown, void>({
      query: () => ({ ...ROUTES.notifications.readAll }),
      onQueryStarted: async (_arg, { dispatch, getState, queryFulfilled }) => {
        const patch = dispatch(
          notificationsApi.util.updateQueryData('getMyNotifications', FIRST_PAGE, (draft) => {
            draft.items.forEach((n) => {
              n.isRead = true;
            });
          }),
        );
        const prev = (getState() as RootState).notifications.unreadCount;
        dispatch(setUnreadCount(0));
        try {
          await queryFulfilled;
        } catch {
          patch.undo(); // the component owns the error toast
          dispatch(setUnreadCount(prev));
        }
      },
    }),
  }),
});

export const {
  useLazyGetMyNotificationsQuery,
  useLazyGetExportDownloadUrlQuery,
  useReadNotificationMutation,
  useReadAllNotificationsMutation,
} = notificationsApi;
